import React, { useCallback, useEffect, useState } from 'react';
import { Settings2, Plus, Trash2, AlertTriangle, ArrowUp, ArrowDown, Lock, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const err = (e) => toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
const swap = (arr, i, j) => { const a = arr.slice(); [a[i], a[j]] = [a[j], a[i]]; return a; };

export const STATE_TONE = {
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200', overdue: 'bg-red-50 text-red-700 border-red-200',
  due_soon: 'bg-amber-50 text-amber-800 border-amber-200', upcoming: 'bg-sky-50 text-sky-700 border-sky-200',
  locked: 'bg-gray-50 text-gray-500 border-gray-200',
};
export const STATE_LABEL = { done: 'Done', overdue: 'Overdue', due_soon: 'Due soon', upcoming: 'Upcoming', locked: 'Waiting' };

const TrackerSettingsDialog = ({ open, onClose, canEdit, onSaved }) => {
  const [s, setS] = useState(null);
  useEffect(() => { if (open) api.get('/api/visa-costs/tracker/settings').then((r) => setS(r.data)).catch(err); }, [open]);
  if (!s) return null;
  const save = async () => {
    try { await api.put('/api/visa-costs/tracker/settings', s); toast({ title: 'Tracker settings saved', description: 'New step order applies to trackers started from now on.' }); onSaved(); onClose(); } catch (e) { err(e); }
  };
  const setStep = (i, patch) => setS({ ...s, steps: s.steps.map((x, n) => (n === i ? { ...x, ...patch } : x)) });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vc-tracker-settings-dialog">
        <DialogHeader><DialogTitle>Renewal steps & alerts</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">Steps run in this order — each unlocks when the one above is done. Due date = renewal date minus "days before".</p>
        <div className="space-y-2">
          {s.steps.map((st, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
              <Input disabled={!canEdit} className="h-9 flex-1" value={st.name} onChange={(e) => setStep(i, { name: e.target.value })} data-testid={`vc-step-name-${i}`} />
              <Input disabled={!canEdit} type="number" min={0} className="h-9 w-16" value={st.days_before} onChange={(e) => setStep(i, { days_before: Number(e.target.value) })} data-testid={`vc-step-days-${i}`} />
              <span className="text-[11px] text-gray-500 w-12 leading-tight">days before</span>
              {canEdit && <>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => setS({ ...s, steps: swap(s.steps, i, i - 1) })} data-testid={`vc-step-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === s.steps.length - 1} onClick={() => setS({ ...s, steps: swap(s.steps, i, i + 1) })} data-testid={`vc-step-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setS({ ...s, steps: s.steps.filter((_, n) => n !== i) })} data-testid={`vc-step-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </>}
            </div>
          ))}
          {canEdit && <Button size="sm" variant="outline" onClick={() => setS({ ...s, steps: [...s.steps, { name: '', days_before: 14 }] })} data-testid="vc-step-add"><Plus className="h-3.5 w-3.5 mr-1" />Add step</Button>}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label>Auto-start tracker <Input disabled={!canEdit} type="number" min={1} className="h-8 w-20 inline-block mx-1" value={s.auto_start_days} onChange={(e) => setS({ ...s, auto_start_days: Number(e.target.value) })} data-testid="vc-tracker-autostart" />days before renewal</label>
          <label>Flag "due soon" <Input disabled={!canEdit} type="number" min={0} className="h-8 w-20 inline-block mx-1" value={s.due_soon_days} onChange={(e) => setS({ ...s, due_soon_days: Number(e.target.value) })} data-testid="vc-tracker-due-soon-days" />days before a step's due date</label>
        </div>
        <p className="text-[11px] text-gray-500">Alerts show on the HR dashboard. You can also set a custom alert date on any step.</p>
        {s.steps.some((x, i) => i > 0 && Number(x.days_before) > Number(s.steps[i - 1].days_before)) && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="vc-step-order-warning">
            A step is due earlier than the step above it. Consider adjusting "days before" so due dates follow the order.
          </p>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button>{canEdit && <Button onClick={save} disabled={!s.steps.length || s.steps.some((x) => !x.name.trim())} data-testid="vc-tracker-settings-save">Save</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StepCost = ({ value, disabled, onSave, testId }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => {
    const next = v === '' ? null : Math.max(0, Number(v));
    if (next !== (value ?? null) && !Number.isNaN(next)) onSave(next);
  };
  return (
    <label className="flex items-center gap-1 text-[11px] text-gray-500" title="Actual cost paid for this step — counts in Actual vs Budget">
      AED
      <input type="number" min={0} step="0.01" disabled={disabled} placeholder="cost" value={v}
        onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        className={`w-24 border rounded px-1.5 py-0.5 text-right ${value != null ? 'border-emerald-300 bg-emerald-50 text-emerald-800 font-medium' : ''}`} data-testid={testId} />
    </label>
  );
};

const StepLine = ({ r, s, i, st, canEdit, call, onMove }) => {
  const code = r.employee_code;
  const steps = r.tracker.steps;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm py-1.5" data-testid={`vc-step-row-${code}-${i}`}>
      <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
      <Checkbox disabled={!canEdit || st.locked} checked={s.done} onCheckedChange={(c) => call(i, { done: !!c })} data-testid={`vc-step-${code}-${i}`} />
      <span className={`min-w-[140px] ${s.done ? 'line-through text-gray-400' : ''}`}>{s.name}</span>
      <StepCost value={s.cost} disabled={!canEdit} onSave={(cost) => call(i, { cost })} testId={`vc-step-cost-${code}-${i}`} />
      <span className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${STATE_TONE[st.state]}`} data-testid={`vc-step-state-${code}-${i}`}>
        {st.locked && <Lock className="h-2.5 w-2.5" />}{STATE_LABEL[st.state]}
      </span>
      {s.done ? <span className="text-[10px] text-gray-400">by {s.done_by}</span> : (
        <div className="flex flex-wrap items-center gap-2 ml-auto text-[11px] text-gray-500">
          <label className="flex items-center gap-1">Due
            <input type="date" disabled={!canEdit} className="border rounded px-1" value={s.due_date} onChange={(e) => e.target.value && call(i, { due_date: e.target.value })} data-testid={`vc-step-due-${code}-${i}`} /></label>
          <label className="flex items-center gap-1" title="Custom date to start alerting (blank = use the 'due soon' days)">Alert from
            <input type="date" disabled={!canEdit} className={`border rounded px-1 ${s.remind_date ? 'border-violet-400 text-violet-700 bg-violet-50' : ''}`} value={s.remind_date || st.remind_from || ''} onChange={(e) => call(i, { remind_date: e.target.value })} data-testid={`vc-step-remind-${code}-${i}`} /></label>
          {s.remind_date ? (
            <span className="flex items-center gap-1 text-violet-700" data-testid={`vc-step-remind-custom-${code}-${i}`}>custom
              {canEdit && <button type="button" className="underline text-gray-500 hover:text-red-600" onClick={() => call(i, { remind_date: '' })} data-testid={`vc-step-remind-clear-${code}-${i}`}>reset</button>}
            </span>
          ) : <span className="text-gray-400">auto</span>}
          {canEdit && <>
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0 || steps[i - 1].done} onClick={() => onMove(i, i - 1)} data-testid={`vc-step-move-up-${code}-${i}`}><ArrowUp className="h-3 w-3" /></Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === steps.length - 1} onClick={() => onMove(i, i + 1)} data-testid={`vc-step-move-down-${code}-${i}`}><ArrowDown className="h-3 w-3" /></Button>
          </>}
        </div>
      )}
    </div>
  );
};

const TrackerRow = ({ r, canEdit, onChanged }) => {
  const p = r.progress;
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  const spent = r.tracker.steps.reduce((a, s) => a + Number(s.cost || 0), 0);
  const base = `/api/visa-costs/records/${r.employee_id}/tracker`;
  const call = async (idx, body) => { try { await api.patch(`${base}/steps/${idx}`, body); onChanged(); } catch (e) { err(e); } };
  const move = async (i, j) => {
    const order = swap(r.tracker.steps.map((_, n) => n), i, j);
    try { await api.put(`${base}/order`, { order }); onChanged(); } catch (e) { err(e); }
  };
  const restart = async () => {
    if (!window.confirm(`Restart ${r.name}'s tracker with the current default steps? Progress on this renewal is cleared.`)) return;
    try { await api.post(`${base}/start`); onChanged(); } catch (e) { err(e); }
  };
  return (
    <div className={`border rounded-lg bg-white p-3 ${p.overdue_steps.length ? 'border-red-300' : p.needs_action ? 'border-amber-300' : ''}`} data-testid={`vc-tracker-row-${r.employee_code}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[200px]"><p className="font-medium">{r.name} <span className="text-xs text-gray-500">{r.employee_code} · {r.department}</span></p>
          <p className="text-xs text-gray-500">Renewal <b>{r.renewal_date}</b> · {p.days_left < 0 ? <span className="text-red-600">{-p.days_left} day(s) overdue</span> : `${p.days_left} day(s) left`}</p></div>
        <div className="flex-1 min-w-[160px]">
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${pct === 100 ? 'bg-emerald-500' : p.overdue_steps.length ? 'bg-red-500' : 'bg-sky-500'}`} style={{ width: `${pct}%` }} /></div>
          <p className="text-[11px] text-gray-500 mt-0.5" data-testid={`vc-tracker-progress-${r.employee_code}`}>{p.done}/{p.total} steps done</p>
        </div>
        <div className="text-right" data-testid={`vc-tracker-spend-${r.employee_code}`}>
          <p className="text-[10px] uppercase text-gray-400">Spent / budget</p>
          <p className="text-sm font-semibold">AED {spent.toLocaleString('en-US', { maximumFractionDigits: 2 })} <span className="text-gray-400 font-normal">/ {Number(r.total || 0).toLocaleString('en-US')}</span></p>
        </div>
        {p.needs_action && <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Needs action</span>}
        {canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={restart} data-testid={`vc-tracker-restart-${r.employee_code}`}><RotateCcw className="h-3 w-3 mr-1" />Restart</Button>}
      </div>
      <div className="mt-2 divide-y">
        {r.tracker.steps.map((s, i) => <StepLine key={`${s.name}-${i}`} r={r} s={s} i={i} st={p.states[i]} canEdit={canEdit} call={call} onMove={move} />)}
      </div>
    </div>
  );
};

export const RenewalTracker = ({ canEdit }) => {
  const [data, setData] = useState({ rows: [], needs_action: 0, settings: null });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => api.get('/api/visa-costs/tracker').then((r) => setData(r.data)).catch(err).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="vc-tracker">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-gray-600">Renewals within <b>{data.settings?.auto_start_days ?? 60}</b> days start automatically · <b className="text-amber-700" data-testid="vc-tracker-needs-action">{data.needs_action}</b> need action</p>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpen(true)} data-testid="vc-tracker-settings-btn"><Settings2 className="h-4 w-4 mr-1" />Steps & alerts</Button>
      </div>
      {loading && <p className="text-sm text-gray-400">Loading tracker…</p>}
      {!loading && !data.rows.length && <p className="text-sm text-gray-500 border rounded-lg bg-white p-6 text-center">No renewals in the tracker window. Use “Track” on a row in the Costs view to start one manually.</p>}
      {data.rows.map((r) => <TrackerRow key={r.employee_id} r={r} canEdit={canEdit} onChanged={load} />)}
      <TrackerSettingsDialog open={open} onClose={() => setOpen(false)} canEdit={canEdit} onSaved={load} />
    </div>
  );
};
